import { useMemo } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { Button } from "@/components/ui/button";
import type { MembershipRole, MembershipStatus } from "@shyne/shared/api";
import type { TeamMember } from "@/repositories/team-members.repository.ts";
import { organization_user_status } from "@/models/organization-user.model.ts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getInitials } from "@/lib/utils.ts";
import { editsOf, editTitleOf, roleLabel, roleOptions } from "@/lib/permissions/member-roles";
import { USER_STATUSES } from "./teammembers.entity";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenuContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

import { useState } from "react";
import { useTeamMembers } from "./teammembers.hooks";
import { MEMBER_SORT_KEY } from "./teammembers.slice";
import StatusBadge from "./teammembers.components/StatusBadge";
import { InviteMemberModal } from "./teammembers.components/InviteMemberModal";
import EditRoleStatusModal from "../teammembers-detail/team-member-detail.components/edit-role-status-modal";

export default function TeamMembersPage() {
  const {
    members,
    loading,
    error,
    administers,
    organizationType,
    total,
    filters,
    pagination,
    handleChangeRole,
    editingMember,
    editingModalOpen,
    saveRoleStatus,
    setEditingModalOpen,
  } = useTeamMembers();
  const [inviteOpen, setInviteOpen] = useState(false);

  const filterItems: FilterItem[] = [
    {
      type: "select",
      title: "Role",
      value: filters.role,
      onValueChange: (role: string) => filters.setRole(role as MembershipRole | ""),
      options: roleOptions(organizationType),
      grid: 2,
    },
    {
      type: "select",
      title: "Status",
      value: filters.status,
      onValueChange: (status: string) => filters.setStatus(status as MembershipStatus | ""),
      options: USER_STATUSES,
      grid: 2,
    },
  ];

  const columns = useMemo<ColumnDef<TeamMember>[]>(() => {
    const editTitleFor = (row: TeamMember) => (administers ? editTitleOf(editsOf(organizationType, row.details?.status)) : null);
    return [
      {
        header: "User",
        accessor: (row) => (
          <Link to={`/settings/team-members/${row.id}`} className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={row.details?.avatar ?? ""} />
              <AvatarFallback>{getInitials(row.name)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{row.name}</div>
              <div className="text-sm text-muted-foreground">{row.email}</div>
            </div>
          </Link>
        ),
        sortable: true,
        sortKey: MEMBER_SORT_KEY,
        cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
      },
      {
        header: "Role",
        accessor: (row) => roleLabel(row.role, organizationType),
      },
      {
        header: "Status",
        accessor: (row) => <StatusBadge status={(row.details?.status ?? organization_user_status.active) as organization_user_status} />,
        className: "whitespace-nowrap",
      },
      {
        header: "Actions",
        accessor: (row) => (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" aria-label={`Actions for ${row.name}`}>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Link to={`/settings/team-members/${row.id}`}>
                  <DropdownMenuItem>View Details</DropdownMenuItem>
                </Link>
                {editTitleFor(row) && (
                  <DropdownMenuItem
                    onSelect={() => {
                      setTimeout(() => handleChangeRole(row), 0);
                    }}
                  >
                    {editTitleFor(row)}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
        className: "whitespace-nowrap",
      },
    ];
  }, [administers, organizationType, handleChangeRole]);

  return (
    <MainLayout
      breadcrumb={[
        { name: "Settings", href: "#" },
        { name: "Team Members", href: "#" },
      ]}
      pageHeader={{
        title: "Team Members",
        subtitle: administers ? "Manage your team members" : "Your team",
        ...(administers ? { buttonText: "+ Invite Member", onButtonClick: () => setInviteOpen(true) } : {}),
      }}
    >
      {error && (
        <p role="alert" className="text-sm text-muted-foreground mb-4">
          {error}
        </p>
      )}
      <SimpleTable<TeamMember>
        columns={columns}
        data={members}
        loading={loading}
        totalCount={total}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        onReset={() => {
          filters.setRole("");
          filters.setStatus("");
        }}
      />

      {administers && <InviteMemberModal open={inviteOpen} onOpenChange={setInviteOpen} />}

      <EditRoleStatusModal
        open={editingModalOpen}
        onOpenChange={setEditingModalOpen}
        organizationType={organizationType}
        currentRole={editingMember?.role ?? "member"}
        currentStatus={editingMember?.details?.status}
        onSubmit={saveRoleStatus}
      />
    </MainLayout>
  );
}
